/**
 * «نسخه‌ی تازه» (owner, 2026-10-16): an app installed on the iPhone home screen stays in memory and keeps
 * running an old build. The bundle knows its own build id; /version.json (never cached) names the live
 * one. When they differ the agent sees ONE banner per new build; «به‌روزرسانی» reloads the app with the
 * new build and fresh data. «بعداً» hides it for that build.
 */
import { RefreshCw, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const SEEN = 'dk-update-dismissed';

function dismissed(build: string): boolean {
  try {
    return window.localStorage.getItem(SEEN) === build;
  } catch {
    return false;
  }
}

export function UpdateBanner() {
  const [live, setLive] = useState<string | null>(null);

  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const r = await fetch('/version.json', { cache: 'no-store' });
        if (!r.ok) return;
        const j = (await r.json()) as { build?: unknown };
        const build = typeof j.build === 'string' ? j.build : null;
        if (!stop && build && build !== __DESK_BUILD__ && !dismissed(build)) setLive(build);
      } catch {
        /* offline / dev server: nothing to offer */
      }
    };
    const t = window.setTimeout(() => void check(), 1500);
    const every = window.setInterval(() => void check(), 10 * 60_000);
    const onShow = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onShow);
    return () => {
      stop = true;
      window.clearTimeout(t);
      window.clearInterval(every);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, []);

  if (!live) return null;
  const later = () => {
    try {
      window.localStorage.setItem(SEEN, live);
    } catch {
      /* UI preference only */
    }
    setLive(null);
  };
  return (
    <div
      role="status"
      data-desk-update
      className="dk-enter fixed inset-x-3 top-[calc(env(safe-area-inset-top)+10px)] z-[80] mx-auto flex max-w-[460px] items-center gap-3 rounded-[20px] border border-accent/40 bg-surface-1 p-3 shadow-float"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent text-on-accent">
        <RefreshCw className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[13.5px] font-extrabold">نسخه‌ی تازه‌ی میز آماده است</p>
        <p className="m-0 text-[12px] leading-6 text-fg-muted">
          برای دیدن تغییرات، به‌روزرسانی کنید.
        </p>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="hx-press min-h-10 shrink-0 cursor-pointer rounded-full bg-accent px-4 text-[13px] font-extrabold text-on-accent shadow-cta"
      >
        به‌روزرسانی
      </button>
      <button
        type="button"
        onClick={later}
        aria-label="بعداً"
        className="grid size-9 shrink-0 cursor-pointer place-items-center rounded-full text-fg-muted hover:bg-surface-2"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}
